const STORAGE_KEY = 'plumaa_prontuario_session_notes';

type ProntuarioSessionNotesEntry = {
	sessionId: string;
	selectedDate: string;
	notes: string;
};

const normalizeSelectedDate = (selectedDate?: string | Date | null): string => {
	if (!selectedDate) return '';
	if (selectedDate instanceof Date) {
		return selectedDate.toISOString();
	}
	return String(selectedDate).trim();
};

const readEntry = (): ProntuarioSessionNotesEntry | null => {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (!raw) return null;

		const parsed: unknown = JSON.parse(raw);
		if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
			return null;
		}

		const record = parsed as Partial<ProntuarioSessionNotesEntry>;
		if (
			typeof record.sessionId !== 'string' ||
			typeof record.selectedDate !== 'string' ||
			typeof record.notes !== 'string'
		) {
			return null;
		}

		return {
			sessionId: record.sessionId,
			selectedDate: record.selectedDate,
			notes: record.notes,
		};
	} catch {
		return null;
	}
};

const writeEntry = (entry: ProntuarioSessionNotesEntry) => {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
	} catch {
		/* ignore quota / private mode */
	}
};

const matchesCurrentSession = (
	entry: ProntuarioSessionNotesEntry,
	sessionId: string,
	selectedDate: string,
): boolean =>
	entry.sessionId === sessionId && entry.selectedDate === selectedDate;

export const peekProntuarioSessionNotes = (
	sessionId: string,
	selectedDate?: string | Date | null,
): string => {
	if (!sessionId) return '';

	const normalizedSelectedDate = normalizeSelectedDate(selectedDate);
	const entry = readEntry();

	if (
		entry &&
		matchesCurrentSession(entry, sessionId, normalizedSelectedDate)
	) {
		return entry.notes;
	}

	return '';
};

export const loadProntuarioSessionNotes = (
	sessionId: string,
	selectedDate?: string | Date | null,
): string => {
	if (!sessionId) return '';

	const normalizedSelectedDate = normalizeSelectedDate(selectedDate);
	const entry = readEntry();

	if (
		entry &&
		matchesCurrentSession(entry, sessionId, normalizedSelectedDate)
	) {
		return entry.notes;
	}

	writeEntry({
		sessionId,
		selectedDate: normalizedSelectedDate,
		notes: '',
	});

	return '';
};

export const saveProntuarioSessionNotes = (
	sessionId: string,
	notes: string,
	selectedDate?: string | Date | null,
): void => {
	if (!sessionId) return;

	writeEntry({
		sessionId,
		selectedDate: normalizeSelectedDate(selectedDate),
		notes,
	});
};
